import React, { Fragment } from 'react'
import { ModalOverlay } from 'common/styleguide/ModalOverlay'
import styles from './PaymentForm.scss'
import { CreditCardForm } from '../CreditCardForm'
import { TotalCalculator } from '../TotalCalculator'
import { UpdatePayment } from '../UpdatePayment'
import { PaymentSuccess } from '../PaymentSuccess'
import { UpsellFooter } from './UpsellFooter'
import { paymentsMessages } from '../intlMessages'
import { RadioButtonGroup } from 'common/styleguide/RadioGroup'
import { Hero } from 'common/styleguide/Hero/Hero'
import { CTA } from 'common/styleguide/CTA'
import { CircleSpinner } from 'common/spinners'
import { errorMessage } from 'lib/styleguide/base.scss'
import { getCurrentUser } from 'lib/utils'
import fetchJSON from 'lib/fetchJSON'
import { Msg, accountLevels, commonMessages } from 'lib/localization'
import { Map } from 'immutable'
import moment from 'moment'
import { FormattedMessage as T, defineMessages } from 'react-intl'

export const messages = defineMessages({
  upToPremium: {
    id: 'PaymentForm.upToPremium',
    defaultMessage: 'Upgrade to Premium'
  },
  upToBasic: {
    id: 'PaymentForm.upToBasic',
    defaultMessage: 'Upgrade to Basic'
  }
})

const APPLE_PAYMENT_TYPE = 3

const paymentStatusEnum = {
  NONE: 'none',
  PENDING: 'pending',
  SUCCESS: 'success',
  DISABLED: 'disabled'
}

// NOTE: baseCostMatrix as returned from the server looks like accountLevelString -> term -> cost in cents
// for ex:
// const baseCostMatrix = {
//   basic: {
//     monthly: 600,
//     yearly: 5000
//   },
//   yearly: {
//     monthly: 1000,
//     yearly: 8000
//   }
// }

// accountLevel -> Map(term -> <span>)
const paymentTermOptions = (matrix, showTrial) => {
  const basicMonthly = matrix['basic']['monthly'] / 100
  const basicYearly = matrix['basic']['yearly'] / 100
  const basicSaved = Math.round(100 * (1 - basicYearly / (basicMonthly * 12)))
  const premiumMonthly = matrix['premium']['monthly'] / 100
  const premiumYearly = matrix['premium']['yearly'] / 100
  const premiumSaved = Math.round(100 * (1 - premiumYearly / (premiumMonthly * 12)))

  return {
    1: Map({
      yearly: <span className={styles.boldLabel}>
        <span className={styles.trial}>
          <T
            id='PaymentForm.annualBillingBasic'
            defaultMessage='Annual Billing (Save {basicSaved}%)'
            values={{ basicSaved }} />
        </span>
      </span>,
      monthly: <span><Msg m={paymentsMessages.monthlyBilling} /></span>
    }),
    2: Map({
      yearly: <span className={styles.boldLabel}>
        {!showTrial &&
          <Fragment>
            <span className={styles.trial}>
              <T
                id='PaymentForm.annualBillingPremium'
                defaultMessage='Annual Billing (Save {premiumSaved}%)'
                values={{ premiumSaved }} />
            </span>
          </Fragment>}
        {showTrial && <span className={styles.trial}>
          <T id='PaymentForm.freeTrial' defaultMessage='Free 7 Day Trial (Annual Billing)' />
        </span>}
      </span>,
      monthly: <span><Msg m={paymentsMessages.monthlyBilling} /></span>
    })
  }
}

export class PaymentForm extends React.PureComponent {
  static defaultProps = {
    currentUser: getCurrentUser()
  }

  constructor (props) {
    super(props)
    const { currentUser } = this.props
    const [accountLevel] = this.parseParams()
    const showTrial = accountLevel === 2 && currentUser.eligibleForOnetimeTrial && (this.props.trial || this.props.location.query.trial)
    this.state = {
      fetchPending: true,
      selectedTerm: 'yearly',
      showCCForm: true,
      paymentStatus: paymentStatusEnum.NONE,
      showTrial,
      isTrial: showTrial
    }
  }

  componentDidMount = () => {
    const { currentUser } = this.props
    const { showTrial } = this.state
    let [accountLevel, accountLevelString] = this.parseParams()

    var fetch = fetchJSON(`/users/${currentUser.id}/upgrading_subscription.json?apikey=x&version=3&accountLevel=${accountLevelString}${showTrial ? '&trial=1' : ''}`)
    Promise.all([fetch, this.injectBraintree()])
      .then(([res, _]) => {
        const ps = res.page_state
        const subscription = ps.subscription && ps.subscription.subscription
        if (res.meta.subscription_present) {
          this.setState({
            monthlyRateAfterDiscounts: ps.monthly_rate_after_discounts,
            yearlyRateAfterDiscounts: ps.yearly_rate_after_discounts,
            isApple: subscription.payment_method_type === APPLE_PAYMENT_TYPE
          })
        }

        if (subscription && subscription.payment_method_type === APPLE_PAYMENT_TYPE) {
          this.setState({ braintreeToken: ps.braintree_token })
        } else {
          this.setupBraintree(ps.braintree_token)
        }

        const baseCostFromRes = res.meta.base_cost_grid
        this.setState({
          baseCostMatrix: baseCostFromRes,
          baseCost: baseCostFromRes[accountLevelString][this.state.selectedTerm] / 100,
          yearlyQuote: ps.yearly_quote,
          yearlyNextBillDate: moment(ps.yearly_next_bill_date),
          monthlyQuote: ps.monthly_quote,
          monthlyNextBillDate: moment(ps.monthly_next_bill_date),
          subscriptionCredit: ps.credit,
          daysLeft: ps.days_left
        })

        if (res.meta.discount_grid_present) {
          this.setState({ discountLineItems: ps.discount_grid })
        }
      })
      .catch(res => {
        console.error(res)
        this.setState({
          error: res.errors[0].error_message,
          paymentStatus: paymentStatusEnum.DISABLED
        })
      })
    this.setState({
      accountLevel: accountLevel,
      accountLevelString: accountLevelString,
      fetchPending: false
    })
  }

  injectBraintree = () => new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = 'https://js.braintreegateway.com/v2/braintree.js'
    script.onload = () => resolve(true)
    document.body.appendChild(script)
  })

  setupBraintree = (token) => {
    window.braintree.setup(token, 'custom', {
      id: 'paymentForm',
      onPaymentMethodReceived: (res) => {
        let cn = res.type !== 'PayPalAccount' && document.getElementById('cardNumber').value
        if (res.type === 'CreditCard' && (cn === null || cn.length === 0)) {
          this.setState({
            error: <T id='PaymentForm.validCCError' defaultMessage='Please enter a valid card number' />
          })
        } else {
          this.submitForm(res.nonce)
        }
      },
      paypal: {
        container: 'paypalContainer',
        onSuccess: () => { this.setState({ showCCForm: false }) },
        onCancelled: () => { this.setState({ showCCForm: true }) }
      }
    })
  }

  parseParams = () => {
    const accountLevelParam = this.props.accountLevel || this.props.location.query.account_level
    return accountLevelParam === 'premium' ? [2, accountLevelParam] : [1, accountLevelParam]
  }

  onRadioChange = (option) => {
    const { accountLevelString, baseCostMatrix, showTrial } = this.state

    this.setState({
      baseCost: baseCostMatrix[accountLevelString][option] / 100,
      selectedTerm: option,
      isTrial: showTrial && option === 'yearly'
    })
  }

  appleContinue = () => {
    // HACK: setupBraintree fails if isApple === true when it's run (bc the form isn't shown)
    const { braintreeToken } = this.state
    this.setupBraintree(braintreeToken)

    this.setState({ isApple: false })
  }

  submitForm = (paymentMethodNonce) => {
    const { userId, selectedTerm, isTrial } = this.state

    const body = {
      user_id: userId,
      billing_type: selectedTerm,
      payment_method_nonce: paymentMethodNonce,
      is_trial: isTrial
    }

    this.setState({ paymentStatus: paymentStatusEnum.PENDING })
    fetchJSON(`/subscriptions/confirmation`, body, { method: 'POST' })
      .then(() => {
        this.setState({ paymentStatus: paymentStatusEnum.SUCCESS })
      })
      .catch(res => {
        this.setState({
          error: res.errors[0].error_message,
          paymentStatus: paymentStatusEnum.NONE
        })
      })
  }

  render () {
    const {
      error,
      fetchPending,
      showCCForm,
      paymentStatus,
      isApple,
      discountLineItems,
      accountLevel,
      baseCost,
      baseCostMatrix,
      selectedTerm,
      subscriptionCredit,
      daysLeft,
      yearlyQuote,
      yearlyNextBillDate,
      monthlyQuote,
      monthlyNextBillDate,
      showTrial,
      isTrial
    } = this.state

    const { currentUser, formOnly, onRequestClose } = this.props

    const showForm = !(fetchPending ||
                       isApple ||
                       paymentStatus === paymentStatusEnum.SUCCESS)
    const paymentDisabled = [paymentStatusEnum.DISABLED,
      paymentStatusEnum.PENDING].indexOf(paymentStatus) >= 0
    const upgrading = (currentUser.accountLevel < accountLevel && subscriptionCredit > 0)

    let ctaText = accountLevel === 2
      ? <Msg m={messages.upToPremium} />
      : <Msg m={messages.upToBasic} />

    const form = <Fragment>
      {fetchPending && <CircleSpinner centered size={48} className={styles.centeredSpinner} />}
      {isApple && <UpdatePayment accountLevel={accountLevel} appleContinue={this.appleContinue} />}
      {paymentStatus === paymentStatusEnum.SUCCESS && <div style={{ display: 'flex', justifyContent: 'space-around' }}><PaymentSuccess onRequestClose={onRequestClose} /></div>}
      {showForm && <div className={styles.container} style={formOnly && { width: 'auto' }}>
        <div className={styles.left}>
          {error && <p className={errorMessage}>{error}</p>}
          <h2><Msg m={paymentsMessages.upgradeAccount} /></h2>
          <div className={styles.line} />
          {upgrading && <div className={styles.explainer}>
            <T id='PaymentForm.upgradeTo'
              defaultMessage="Upgrade to a {accountLevel} subscription today, and we'll prorate the charge to reflect your remaining subscription term."
              description='accountLevel will either be "Premium" or "Basic"'
              values={{ accountLevel: <Msg m={accountLevel === 2 ? accountLevels.premium : accountLevels.basic} /> }} />
          </div>}
          <form id='paymentForm'>
            <div className={styles.rBGContainer}>
              {baseCostMatrix && <RadioButtonGroup
                options={paymentTermOptions(baseCostMatrix, showTrial)[accountLevel]}
                selected={selectedTerm}
                onChange={this.onRadioChange} />}
            </div>

            {showCCForm && <CreditCardForm />}

            <div className={styles.goButtons}>
              <CTA type='submit' disabled={paymentDisabled} className={styles.topGoButton} button>
                {paymentStatus === paymentStatusEnum.PENDING
                  ? <CircleSpinner light size={12} />
                  : ctaText}
              </CTA>
              {showCCForm && <span><Msg m={commonMessages.or} /></span>}
              <div id='paypalContainer' className={styles.paypalContainer} />
            </div>
          </form>
        </div>
        <div className={styles.right}>
          <TotalCalculator
            isTrial={isTrial}
            accountLevelString={accountLevel === 1 ? 'basic' : 'premium'}
            term={selectedTerm}
            yearlyQuote={yearlyQuote}
            monthlyQuote={monthlyQuote}
            nextBillDate={selectedTerm === 'yearly' ? yearlyNextBillDate : monthlyNextBillDate}
            subscriptionCredit={subscriptionCredit}
            daysLeft={daysLeft}
            baseCost={baseCost}
            discountLineItems={discountLineItems} />
        </div>
      </div>}
    </Fragment>

    if (formOnly) return form

    return (
      <div className={styles.PaymentForm}>
        <Hero
          img={'/images/revised_layout/signup-bg.jpg'}
          img2x={'/images/revised_layout/signup-bg.jpg'}
          marginBottom={'0px'}
          blur
          className={styles.hero}>
          {form}
        </Hero>
        <UpsellFooter accountLevel={accountLevel} />
      </div>
    )
  }
}

export const ModalPaymentForm = ({ onRequestClose, reloadUser }) => {
  const reloadAndClose = () => { reloadUser && reloadUser(); onRequestClose() }
  return <ModalOverlay contentPadding={false} shouldCloseOnOverlayClick={false} onRequestClose={reloadAndClose} maxWidth='775px'>
    <PaymentForm accountLevel='premium' trial={getCurrentUser().eligibleForOnetimeTrial} formOnly onRequestClose={reloadAndClose} />
  </ModalOverlay>
}
